import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import {
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
} from '@mui/material';

interface FormFieldProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel';
  multiline?: boolean;
  rows?: number;
  select?: boolean;
  options?: { value: string | number; label: string }[];
  radio?: boolean;
  radioOptions?: { value: string | number; label: string }[];
  checkbox?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

export const FormField = <T extends FieldValues>({
  name,
  control,
  label,
  type = 'text',
  multiline = false,
  rows = 1,
  select = false,
  options = [],
  radio = false,
  radioOptions = [],
  checkbox = false,
  disabled = false,
  placeholder,
}: FormFieldProps<T>) => {
  if (checkbox) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl error={!!error}>
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={field.value || false}
                  disabled={disabled}
                />
              }
              label={label}
            />
            {error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        )}
      />
    );
  }

  if (radio) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl error={!!error}>
            <FormLabel component="legend">{label}</FormLabel>
            <RadioGroup {...field} row>
              {radioOptions.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
            {error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        )}
      />
    );
  }

  if (select) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth error={!!error}>
            <InputLabel>{label}</InputLabel>
            <Select
              {...field}
              label={label}
              disabled={disabled}
              value={field.value || ''}
            >
              {options.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        )}
      />
    );
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          fullWidth
          label={label}
          type={type}
          multiline={multiline}
          rows={multiline ? rows : undefined}
          disabled={disabled}
          placeholder={placeholder}
          error={!!error}
          helperText={error?.message}
        />
      )}
    />
  );
};
